// @flow

import invariant from 'invariant';
import debounce from 'lodash/debounce';
import flow from 'lodash/flow';
import snakeCase from 'lodash/snakeCase';
import { type Store } from 'redux';

import { debugPersist as debug } from 'Lib/debug';
import { type Action, type GlobalStateType } from 'Lib/reduxType';

import { type Lifecycle, createLifecycle } from 'Store/lifecycles';

import { persist, rehydrate, rehydrated } from './actions';
import {
  FLUSH,
  INVALID_STORAGE_GRACE_PERIOD,
  METADATA_KEY,
  PERSIST,
  REHYDRATE,
  STORAGE_PREFIX,
} from './constants';
import defaultMigrate from './migrate';
import defaultReconciler from './reconciler';
import defaultShouldPersist from './shouldPersist';

function defaultSerialize(state: GlobalStateType): string {
  return JSON.stringify(state);
}

function defaultDeserialize(state: string): GlobalStateType {
  return JSON.parse(state);
}

/**
 * Metadata contains the version of the persisted slice for invalidation
 */
const createAddMetadata = (metadata: Function | Object) => (inputState) => {
  try {
    const state = { ...inputState };
    state[METADATA_KEY] = typeof metadata === 'function' ? metadata(inputState) : metadata;
    return state;
  } catch (e) {
    debug('addMetadata failed');
    return inputState;
  }
};

const createRemoveMetadata = (onMetaData) => (inputState) => {
  if (!(METADATA_KEY in inputState)) {
    onMetaData(null);
    return inputState;
  }
  try {
    const state = { ...inputState };
    onMetaData(state[METADATA_KEY]);
    delete state[METADATA_KEY];
    return state;
  } catch (e) {
    debug('removeMetadata failed');
    return inputState;
  }
};

export default function createPersistableReducer(baseConfig: any = {}) {
  const {
    getStorageKey = (key) => `${STORAGE_PREFIX}${key}`,
    getTimestamp = () => Date.now(),
    gracePeriod = INVALID_STORAGE_GRACE_PERIOD,
    storage: baseStorage,
  } = baseConfig;

  const pendingSlices = new Set();
  const storageKeys = new Set();

  let _store;

  /**
   * Hooks into the store to create a `persist` function that can be used to activate persistence
   * behavior in any `persistReducer()`-ed reducers.
   */
  function persistStore(store: Store<GlobalStateType, { type: string }>) {
    _store = store;
    return {
      initializePersist() {
        _store.dispatch(persist());
      },
    };
  }

  function rehydrateComplete() {
    if (!pendingSlices.size) {
      debug('[rehydrated] pending slices done');
      _store.dispatch(rehydrated());
    }
  }

  const rehydrateSlice = (key, storage, deserialize, getMetaData) => {
    pendingSlices.add(key);

    return storage.getItem(getStorageKey(key)).then((serializedState) => {
      invariant(_store, 'No store specified!');

      let deserializedState;
      try {
        deserializedState = deserialize(serializedState);
      } catch (e) {
        deserializedState = undefined;
      }

      _store.dispatch(rehydrate(key, deserializedState, getMetaData()));

      pendingSlices.delete(key);
      rehydrateComplete();

      return deserializedState;
    });
  };

  /**
   * Wraps a reducer so that it can handle persistence actions and save/load from storage
   */
  function persistReducer(
    persistConfig: Object,
    reducer: (GlobalStateType, Action) => GlobalStateType
  ): (GlobalStateType, Action) => GlobalStateType {
    const config = {
      transforms: [],
      migrate: defaultMigrate,
      serialize: defaultSerialize,
      deserialize: defaultDeserialize,
      reconciler: defaultReconciler,
      shouldPersist: defaultShouldPersist,
      wait: 1000,
      maxWait: 1000,
      ...baseConfig,
      ...persistConfig,
    };

    const { key, storage, migrate, reconciler, shouldPersist, wait, maxWait, version = 0 } = config;

    invariant(key, 'persistReducer requires a key');
    invariant(storage, 'persistReducer requires a storage mechanism');

    const storageKey = getStorageKey(key);
    if (storage === baseStorage) {
      storageKeys.add(storageKey);
    }

    let persistedMetadata;
    const onMetaData = (metadata) => {
      persistedMetadata = metadata;
    };
    const getMetaData = () => persistedMetadata || {};

    const serialize = flow(
      [
        ...config.transforms.map((transform) => transform.in),
        createAddMetadata(() => ({ version, timestamp: getTimestamp() })),
        config.serialize,
      ].filter(Boolean)
    );

    // When deserializing, the transforms run in REVERSE order
    const deserialize = flow(
      [
        ...config.transforms.map((transform) => transform.out),
        createRemoveMetadata(onMetaData),
        config.deserialize,
      ]
        .filter(Boolean)
        .reverse()
    );

    let isRehydrated = false;
    let needsPersist = true;

    const debouncedPersist = debounce(
      (state) => {
        try {
          const serializedState = serialize(state);
          return storage.setItem(storageKey, serializedState).then(() => {
            needsPersist = false;
            debug(`[store] ${key}`);
          });
        } catch (e) {
          debug(`[error] ${key}`);
        }
        return Promise.resolve();
      },
      wait,
      { maxWait }
    );

    const maybePersist = (prevState, nextState) => {
      if (needsPersist || shouldPersist(prevState, nextState)) {
        if (isRehydrated) {
          debouncedPersist(nextState);
        } else {
          debug(`[wait] ${key} is not yet rehydrated`);
        }
      }
    };

    return (state: GlobalStateType, action: any) => {
      const reducedState = reducer(state, action);
      if (!_store) {
        return reducedState;
      }

      switch (action.type) {
        case FLUSH: {
          debouncedPersist.flush();
          return reducedState;
        }

        case PERSIST: {
          rehydrateSlice(key, storage, deserialize, getMetaData);
          return reducedState;
        }

        case REHYDRATE: {
          if (action.payload.key !== key) {
            return reducedState;
          }

          // Replacing the reducer from dynamic reducers will cause multiple REHYDRATE actions
          if (isRehydrated) {
            debug(`[skip] ${key} is already rehydrated`);
            return reducedState;
          }

          isRehydrated = true;
          debug(`[rehydrate] ${key}`);

          // It's generally NOT a good idea for a reducer to modify the state on REHYDRATE
          if (state !== reducedState) {
            debug(`[warn] ${key} was modified by ${REHYDRATE}`);
          }

          // Migrate the persisted state as needed
          const migratedState = migrate
            ? migrate(action.payload.data, action.payload.version, version)
            : action.payload.data;

          // Did the migration update anything?
          if (migratedState !== action.payload.data) {
            if (migratedState) {
              debug(`[migrate] ${key} from version ${action.version} to ${version}`);
            } else {
              debug(`[refresh] ${key} version mismatch ${action.version}`);
            }
          }

          // Reconcile the persisted state and the current reducer state
          const reconciledState = migratedState
            ? reconciler(migratedState, reducedState)
            : reducedState;

          maybePersist(state, reconciledState);

          return reconciledState;
        }

        default: {
          // The reducer is invoked with an undefined state for internal Redux actions.
          // We do NOT want to persist directly after those actions!
          if (typeof state !== 'undefined') {
            maybePersist(state, reducedState);
          }
          return reducedState;
        }
      }
    };
  }

  /**
   * Generate lifecycles that can be used to ensure particular slices are rehydrated
   */
  const lifecycles: Map<string, Lifecycle> = new Map();
  function getRehydrateLifecycle(storeKey?: string): Lifecycle {
    const key = storeKey ? `REHYDRATE_${snakeCase(storeKey).toUpperCase()}` : 'REHYDRATED_COMPLETE';
    const lifecycle = lifecycles.get(key) || createLifecycle(key);
    if (!lifecycles.has(key)) {
      lifecycles.set(key, lifecycle);
    }
    return lifecycle;
  }

  /**
   * Purge old or invalid storage keys
   */
  async function storageCleanup() {
    const allStorageKeys = await baseStorage.getAllKeys();

    return Promise.all([
      // Clean up old redux-persist keys
      ...allStorageKeys
        .filter((key) => key.startsWith('reduxPersist:'))
        .map((key) =>
          baseStorage
            .removeItem(key)
            .then(() => key)
            .catch(() => null)
        ),

      // Cleans up keys that aren't in use
      ...allStorageKeys
        .filter((key) => !storageKeys.has(key) && key.startsWith(STORAGE_PREFIX))
        .map((key) =>
          baseStorage.getItem(key).then((serializedState) => {
            // This persisted key does not seem to be registered. If it's stale, remove it.
            try {
              const { [METADATA_KEY]: metadata } = defaultDeserialize(serializedState);
              const timestamp: number =
                typeof metadata.timestamp === 'number' ? metadata.timestamp : 0;
              if (timestamp && getTimestamp() - timestamp < gracePeriod) {
                return Promise.resolve(null);
              }
            } catch (e) {
              // Something went wrong trying to deserialize
            }
            return baseStorage
              .removeItem(key)
              .then(() => key)
              .catch(() => null);
          })
        ),
    ]).then((removedKeys) => removedKeys.filter((v) => v));
  }

  return {
    getRehydrateLifecycle,
    persistReducer,
    persistStore,
    storageCleanup,
  };
}
